"use client";

import { useState } from "react";
import History from "lucide-react/dist/esm/icons/history";
import Download from "lucide-react/dist/esm/icons/download";
import FileText from "lucide-react/dist/esm/icons/file-text";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import { format } from "date-fns";
import { useAssetVersions } from "@/hooks/useAssetVersions";
import { useAssetDownload } from "@/hooks/useAssetDownload";
import { formatFileSize } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface VersionHistoryProps {
  assetId: string;
  currentVersion: string;
  hasAccess?: boolean;
}

export function VersionHistory({ assetId, currentVersion, hasAccess = false }: VersionHistoryProps) {
  const { data: versions, isLoading } = useAssetVersions(assetId);
  const { downloadAsset, isDownloading } = useAssetDownload();
  const [downloadingVersionId, setDownloadingVersionId] = useState<string | null>(null);

  const handleDownloadVersion = async (versionId: string) => {
    setDownloadingVersionId(versionId);
    await downloadAsset(assetId, versionId);
    setDownloadingVersionId(null);
  };

  if (isLoading) {
    return (
      <div className="py-4 space-y-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!versions || versions.length === 0) {
    return null;
  }

  return (
    <AccordionItem value="versions">
      <AccordionTrigger className="text-lg font-semibold">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4" />
          Version History
          <Badge variant="secondary" className="ml-2 text-xs">
            {versions.length} {versions.length === 1 ? "release" : "releases"}
          </Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <ScrollArea className={versions.length > 3 ? "h-[300px]" : ""}>
          <div className="space-y-4 pr-4">
            {versions.map((version, index) => (
              <div
                key={version.id}
                className="relative pl-6 pb-4 last:pb-0"
              >
                {/* Timeline line */}
                {index < versions.length - 1 && (
                  <div className="absolute left-[9px] top-6 bottom-0 w-px bg-border" />
                )}
                
                {/* Timeline dot */}
                <div
                  className={`absolute left-0 top-1.5 h-[18px] w-[18px] rounded-full border-2 ${
                    version.version === currentVersion
                      ? "bg-primary border-primary"
                      : "bg-background border-muted-foreground/30"
                  }`}
                />

                <div className="space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">v{version.version}</span>
                      {version.version === currentVersion && (
                        <Badge variant="default" className="text-xs">
                          Latest
                        </Badge>
                      )}
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(version.created_at), "MMM d, yyyy")}
                      </span>
                    </div>

                    {/* Download button for buyers */}
                    {hasAccess && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadVersion(version.id)}
                        disabled={isDownloading && downloadingVersionId === version.id}
                        className="h-7 text-xs"
                      >
                        {isDownloading && downloadingVersionId === version.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Download className="h-3 w-3 mr-1" />
                            Download
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {version.file_size && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Download className="h-3 w-3" />
                      {formatFileSize(version.file_size)}
                    </div>
                  )}

                  {version.changelog && (
                    <div className="mt-2 p-3 bg-muted/50 rounded-md">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                        <FileText className="h-3 w-3" />
                        What's new
                      </div>
                      <p className="text-sm whitespace-pre-wrap">
                        {version.changelog}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </AccordionContent>
    </AccordionItem>
  );
}
